import { useMemo, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { publishedGuides } from "@/data/learnGuides";

const CATEGORIES = [
  "All",
  "Metabolic",
  "Tissue & Cellular",
  "GH Axis",
  "Neuro",
  "Melanocortin",
  "Mitochondrial",
  "Peptide Blends",
];

// Legacy ?category= aliases. Old inbound URLs / bookmarks continue to
// resolve to the correct new label rather than 404'ing or showing an
// empty list, the URL is rewritten in place so the pill state and the
// query string stay in sync.
const CATEGORY_ALIASES = {
  "Weight management": "Metabolic",
  "Tissue recovery": "Tissue & Cellular",
  Blends: "Peptide Blends",
};

// FAQ block rendered on the canonical /peptides hub view only. Kept as a
// module-level constant so the identical Q&A content can be reused inside
// the FAQPage JSON-LD emitted from the same page.
const CATALOG_FAQ = [
  {
    q: "What are research peptides?",
    a: "Peptides are short chains of amino acids. Research peptides are peptide (and related) materials supplied for laboratory and experimental investigation. PepDirect research peptides are supplied for that context only and are not intended for human consumption, clinical use or self-administration.",
  },
  {
    q: "Can I buy research peptides from PepDirect in the UK?",
    a: "Yes. PepDirect offers a UK catalogue of research peptides and related laboratory compounds available for laboratory research purposes only. Ordering, format selection and lot documentation are handled through the individual product pages.",
  },
  {
    q: "Does PepDirect provide peptide COAs?",
    a: "Lot-specific analytical documentation is provided for applicable research peptides through the PepDirect COA Library. A COA describes the tests performed on a specific batch, see the COA reading guide for what a purity result does and does not establish.",
  },
  {
    q: "What is the difference between a raw vial and a research pen kit?",
    a: "A raw vial is supplied as lyophilised research material in a sealed vial format. A research pen kit is supplied in a prepared pre-filled research-device format. Both are product-format descriptions only, compound identity and lot documentation are the same source of truth on the relevant product page.",
  },
  {
    q: "How can I compare different research peptides?",
    a: "Each product page publishes compound-specific information, chemical identity, format, and lot-linked analytical documentation. The PepDirect Research Guide Library also publishes compound overviews and structured comparisons that can be used alongside the product data.",
  },
  {
    q: "Are PepDirect peptides intended for human use?",
    a: "No. All PepDirect research peptides are supplied strictly for laboratory research use only. They are not marketed as medicines, food supplements, cosmetics or products for human or veterinary use.",
  },
];

const FORMATS = [
  { id: "all", label: "All" },
  { id: "pen", label: "Pen Kits" },
  { id: "raw", label: "Raw Vials" },
];

// Fallback copy for the two format-selector cards. Each field can be
// overridden by GET /site-settings/best-sellers-content (see /admin's
// Best Sellers editor); anything missing falls back to these values so
// the general catalog view stays visually identical to what it was
// before this became admin-editable.
const FORMAT_CARD_DEFAULTS = [
  {
    format: "pen",
    testId: "format-card-pen",
    label: "● Pen kit",
    headline: "Pre-filled research format",
    bullets: [
      "Pre-filled device format",
      "Fixed click-increment mechanism",
      "No raw-powder reconstitution step",
      "Storage requirements shown per product",
    ],
    closing: "Explore Pen Kits →",
  },
  {
    format: "raw",
    testId: "format-card-raw",
    label: "● Raw vial",
    headline: "Lyophilised vial format",
    bullets: [
      "Lyophilised research material",
      "Defined amount shown per vial",
      "Requires laboratory reconstitution",
      "Storage requirements shown per compound",
    ],
    closing: "Explore Raw Vials →",
  },
];

// Shared category renderer. `fixedFormat` is passed in from /pen-kits,
// /vial-kits, and /raw-vials so those pages arrive with the matching
// format-selector card highlighted. On those routes clicking a sibling
// card navigates to that format's dedicated route; the Format pill row
// is hidden since the URL already announces the format. `bestOnly` filters
// to is_best_seller products so the top-nav "Best Sellers" tab points here.
export default function CatalogPage({ fixedFormat = null, title = "All Peptides", eyebrow = "↳ Research catalog", intro }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const rawCategory = params.get("category") || "All";
  const category = CATEGORY_ALIASES[rawCategory] || rawCategory;

  // Legacy handoff: existing bookmarks that carry the old category label
  // ("Weight management", "Tissue recovery", "Blends") are rewritten to
  // the new canonical taxonomy in place. The user stays on /peptides so
  // this doesn't spawn a redirect chain and the new pill highlights.
  useEffect(() => {
    if (CATEGORY_ALIASES[rawCategory]) {
      const next = new URLSearchParams(params);
      next.set("category", CATEGORY_ALIASES[rawCategory]);
      setParams(next, { replace: true });
    }
  }, [rawCategory, params, setParams]);

  const format = fixedFormat || (params.get("format") || "all").toLowerCase();
  const q = (params.get("q") || "").toLowerCase();
  const bestOnly = params.get("best") === "1";

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products-all"],
    queryFn: async () => (await api.get("/products")).data,
  });

  // Admin-editable header + format card copy for /peptides?best=1. Only
  // fetched when we're actually on the best-only view so other catalog
  // routes stay fast. Falls back to a sensible default when the request
  // hasn't returned yet.
  const { data: bestContent } = useQuery({
    queryKey: ["site-settings", "best-sellers-content"],
    queryFn: async () => (await api.get(`/site-settings/best-sellers-content`)).data,
    enabled: bestOnly,
  });
  const cardsBySettings = bestContent?.cards || [];
  const cardFor = (fmt) => cardsBySettings.find((c) => c.format === fmt) || null;

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (category !== "All" && !(p.categories || []).includes(category)) return false;
      if (format !== "all" && p.format !== format) return false;
      if (bestOnly && !p.is_best_seller) return false;
      if (
        q &&
        !(
          p.name.toLowerCase().includes(q) ||
          (p.cas_number || "").toLowerCase().includes(q) ||
          (p.short_description || "").toLowerCase().includes(q)
        )
      )
        return false;
      return true;
    });
  }, [products, category, format, q, bestOnly]);

  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c === "All") next.delete("category");
    else next.set("category", c);
    setParams(next);
  };

  const setFormat = (f) => {
    const next = new URLSearchParams(params);
    if (f === "all") next.delete("format");
    else next.set("format", f);
    setParams(next);
  };

  const showFormatUi = !fixedFormat;
  // Data-driven Research Guides pill list, reads from LEARN_GUIDES so new
  // published guides appear here automatically without touching CatalogPage.
  const catalogGuides = useMemo(() => publishedGuides(), []);
  // Dedicated /pen-kits, /vial-kits, /raw-vials routes still render the
  // format helper so the matching card can carry its selected highlight,
  // but cards navigate to their sibling routes instead of mutating a
  // query param (the fixedFormat prop pins the filter here).
  const onFormatCardClick = (fmt) => {
    if (fixedFormat) {
      const routeFor = { pen: "/pen-kits", raw: "/raw-vials" };
      navigate(routeFor[fmt]);
      return;
    }
    setFormat(format === fmt ? "all" : fmt);
  };

  // Dynamic page title. When the URL carries a category filter (e.g.
  // /peptides?category=Metabolic) show "{Category} Peptides" so the page
  // header reflects what the visitor actually clicked, instead of always
  // announcing "All Peptides" regardless of entry point. `title` prop
  // still takes precedence for the dedicated /pen-kits and /raw-vials
  // routes, and `bestOnly` continues to override with "Best Sellers".
  const dynamicTitle = (() => {
    if (title !== "All Peptides") return title; // dedicated wrapper route
    if (bestOnly) return bestContent?.title || "Best Sellers";
    if (category && category !== "All") {
      // Category labels are already the canonical taxonomy (Title Case
      // with intentional casing like "GH Axis"). Some labels already
      // carry the word "Peptides" (e.g. "Peptide Blends"), don't
      // suffix again in that case to avoid "Peptide Blends Peptides".
      if (/peptide/i.test(category)) return category;
      return `${category} Peptides`;
    }
    return "Research Peptides UK";
  })();

  // Detect the "canonical /peptides hub" view: only then do we render the
  // long-form SEO intro + bottom sections + CollectionPage JSON-LD. Any
  // filter (category / format / best / search) or dedicated wrapper route
  // suppresses these so filtered views don't compete for the same canonical.
  const isCanonicalHub =
    !fixedFormat &&
    !bestOnly &&
    !q &&
    (rawCategory === "All" || !rawCategory) &&
    (!params.get("format") || params.get("format") === "all") &&
    title === "All Peptides";

  const dynamicIntro =
    bestOnly && bestContent?.description
      ? bestContent.description
      : isCanonicalHub
        ? "PepDirect supplies a UK catalogue of research peptides and related laboratory materials for analytical and experimental research. The range includes individual peptides, peptide blends, raw vial formats and selected pre-filled research pen kits. Researchers can browse compounds across metabolic, tissue and cellular, GH-axis, neuro, melanocortin and mitochondrial research categories, with lot-specific analytical documentation available through the PepDirect COA Library. The catalogue is designed to make it straightforward to compare chemical identity, available formats and current stock while keeping supporting research information, compound guides and analytical methodology, separate from the commercial product data. Researchers looking to buy research peptides UK-side can move directly from a catalogue listing to the underlying research guide or Certificate of Analysis. All PepDirect peptide products are supplied strictly for laboratory research use and are not intended for human use."
        : intro || "Every compound with its certificate of analysis. Shipping calculated at checkout.";

  // Canonical + JSON-LD only for the hub view. Non-hub filtered variants
  // keep the app-level canonical (which points at the base /peptides) so
  // filter permutations don't spawn independent indexable pages.
  useEffect(() => {
    if (!isCanonicalHub) return;
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://pepdirect.co/peptides");
    // CollectionPage + ItemList + BreadcrumbList JSON-LD. Reuses the
    // already-fetched products query, no extra network. Only live
    // products from the catalog appear here, so withdrawn SKUs (which
    // never come back from the API) stay out of the schema by design.
    const collectionLd = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Research Peptides UK",
      url: "https://pepdirect.co/peptides",
      description:
        "Research peptides and related laboratory compounds supplied by PepDirect UK, with lot-specific testing and Certificate of Analysis documentation where available.",
      isPartOf: { "@type": "WebSite", name: "PepDirect", url: "https://pepdirect.co" },
      mainEntity: {
        "@type": "ItemList",
        itemListElement: (products || []).map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `https://pepdirect.co/product/${p.slug}`,
          name: p.name,
        })),
      },
    };
    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://pepdirect.co/" },
        { "@type": "ListItem", position: 2, name: "Peptides", item: "https://pepdirect.co/peptides" },
      ],
    };
    // FAQPage JSON-LD mirrors CATALOG_FAQ verbatim, visible Q&A on the
    // page and schema entries stay identical (no hidden or embellished
    // answers, which is a Google policy requirement for rich results).
    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: CATALOG_FAQ.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    };
    const write = (id, obj) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(obj);
    };
    write("catalog-collection-ld", collectionLd);
    write("catalog-breadcrumb-ld", breadcrumbLd);
    write("catalog-faq-ld", faqLd);
    return () => {
      document.getElementById("catalog-collection-ld")?.remove();
      document.getElementById("catalog-breadcrumb-ld")?.remove();
      document.getElementById("catalog-faq-ld")?.remove();
    };
  }, [isCanonicalHub, products]);

  return (
    <div className="container-pd py-12 lg:py-16" data-testid="catalog-page">
      <p className="label-mono text-brandblue">{isCanonicalHub ? "↳ Research catalogue" : eyebrow}</p>
      <h1 className="mt-3 font-heading text-4xl font-black tracking-tight text-ink sm:text-5xl" data-testid="catalog-title">
        {dynamicTitle}
      </h1>
      <p className="mt-3 max-w-3xl text-base text-subtle">
        {dynamicIntro}
      </p>

      {q && <p className="mt-4 text-sm text-subtle">Search results for “{q}”</p>}

      <div className="mt-8 grid grid-cols-1 gap-4 rounded-2xl panel-warm bg-white p-5 sm:grid-cols-2" data-testid="format-helper">
            {/* Highlight tracks the current filter selection only, no card
                carries a permanent default highlight. Selecting "All" leaves
                all three cards in their idle state. When we're on the
                /peptides?best=1 view the copy is overlaid from the admin
                site_settings blob; other views keep the built-in fallbacks. */}
            {FORMAT_CARD_DEFAULTS.map((base) => {
              const override = cardFor(base.format);
              const label = override?.label || base.label;
              const headline = override?.headline || base.headline;
              const bullets = override?.bullets?.length ? override.bullets : base.bullets;
              const closing = override?.closing || base.closing;
              const isActive = format === base.format;
              return (
                <button
                  key={base.format}
                  type="button"
                  onClick={() => onFormatCardClick(base.format)}
                  className={`group relative rounded-xl p-5 text-left transition-colors ${
                    isActive
                      ? "border-2 border-brandblue/40 bg-brandbluelight"
                      : "border border-line hover:border-brandblue/40 hover:bg-brandbluelight/40"
                  }`}
                  data-testid={base.testId}
                  aria-pressed={isActive}
                >
                  {isActive && (
                    <span className="absolute right-4 top-4 rounded-full bg-brandblue px-2 py-0.5 font-mono text-[9px] tracking-[0.12em] text-white">SELECTED</span>
                  )}
                  <p className="label-mono text-navy">{label}</p>
                  <p className="mt-2 text-sm font-semibold text-ink">{headline}</p>
                  <ul className="mt-3 space-y-2 text-sm text-ink">
                    {bullets.map((b) => (
                      <li key={b} className="flex items-center gap-2">
                        <Check className="h-4 w-4 shrink-0 text-brandblue" strokeWidth={2.5} />
                        {b}
                      </li>
                    ))}
                  </ul>
                  {closing && (
                    <p className="mt-4 text-sm font-semibold text-brandblue">{closing}</p>
                  )}
                </button>
              );
            })}
          </div>

          {showFormatUi && (
          <div className="mt-6">
            <p className="label-mono mb-2">Format</p>
            <div className="flex flex-wrap gap-2">
              {FORMATS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFormat(f.id)}
                  className={`btn-pill border px-5 py-2 text-sm font-semibold transition-colors ${
                    format === f.id ? "border-brandblue bg-brandblue text-white" : "panel-warm bg-white text-ink hover:border-navy"
                  }`}
                  data-testid={`format-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
              <Link
                to="/accessories"
                className="btn-pill btn-outline px-5 py-2 text-sm font-semibold"
                data-testid="format-accessories"
              >
                Accessories →
              </Link>
            </div>
          </div>
          )}

      <div className={showFormatUi ? "mt-5" : "mt-8"}>
        <p className="label-mono mb-2">Category</p>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`btn-pill border px-5 py-2 text-sm font-semibold transition-colors ${
                category === c ? "border-navy bg-navy text-white" : "panel-warm bg-white text-ink hover:border-navy"
              }`}
              data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="py-24 text-center text-subtle">Loading catalog…</div>
      ) : (
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
      {!isLoading && filtered.length === 0 && (
        <div className="py-24 text-center text-subtle">No peptides match your search.</div>
      )}

      {/* SEO informational sections, only on the canonical /peptides hub.
          Filtered variants keep the page purely commercial. */}
      {isCanonicalHub && !isLoading && (
        <div className="mt-20 space-y-16" data-testid="catalog-seo-content">
          <section data-testid="catalog-seo-overview">
            <p className="label-mono text-brandblue">§ UK catalogue</p>
            <h2 className="mt-3 font-heading text-2xl font-black tracking-tight text-ink sm:text-3xl">
              Research Peptides Available in the UK
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-subtle">
              The PepDirect UK catalogue lists individual research peptides, multi-component
              peptide blends, lyophilised raw vials and selected pre-filled research pen-kit
              formats. Not every compound is available in every format, the format shown on
              each product page is the definitive answer for that item.
            </p>
            <p className="mt-3 max-w-3xl text-base leading-relaxed text-subtle">
              Researchers looking to buy research peptides through the UK catalogue can
              compare compound identity, format and current stock across categories from a
              single canonical catalogue page, with supporting compound information kept
              separate from the commercial listings.
            </p>
          </section>

          <section data-testid="catalog-seo-taxonomy">
            <p className="label-mono text-brandblue">§ Browse by research area</p>
            <h2 className="mt-3 font-heading text-2xl font-black tracking-tight text-ink sm:text-3xl">
              Browse Peptides by Research Area
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-subtle">
              The catalogue is organised around broad research areas. These labels describe
              the research context each compound sits in, not consumer-benefit categories.
            </p>
            <dl className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-xl panel-warm bg-white p-5">
                <dt className="font-heading text-base font-black text-ink">Metabolic</dt>
                <dd className="mt-2 text-sm leading-relaxed text-subtle">
                  Research compounds investigated at metabolic and endocrine pathways, for example{" "}
                  <Link to="/product/retatrutide" className="text-brandblue underline underline-offset-2 hover:no-underline">Retatrutide</Link>{" "}
                  and{" "}
                  <Link to="/product/cagrilintide" className="text-brandblue underline underline-offset-2 hover:no-underline">Cagrilintide</Link>.
                </dd>
              </div>
              <div className="rounded-xl panel-warm bg-white p-5">
                <dt className="font-heading text-base font-black text-ink">Tissue &amp; Cellular</dt>
                <dd className="mt-2 text-sm leading-relaxed text-subtle">
                  Compounds studied in tissue-repair, connective-tissue and cellular research
                  contexts, including{" "}
                  <Link to="/product/bpc-157" className="text-brandblue underline underline-offset-2 hover:no-underline">BPC-157</Link>{" "}
                  and{" "}
                  <Link to="/product/tb-500" className="text-brandblue underline underline-offset-2 hover:no-underline">TB-500</Link>.
                </dd>
              </div>
              <div className="rounded-xl panel-warm bg-white p-5">
                <dt className="font-heading text-base font-black text-ink">GH Axis</dt>
                <dd className="mt-2 text-sm leading-relaxed text-subtle">
                  Research compounds associated with GHRH or growth-hormone-secretagogue
                  pathway research, see the{" "}
                  <Link to="/learn/what-is-cjc-1295" className="text-brandblue underline underline-offset-2 hover:no-underline">CJC-1295</Link>{" "}
                  and{" "}
                  <Link to="/learn/what-is-ipamorelin" className="text-brandblue underline underline-offset-2 hover:no-underline">Ipamorelin</Link>{" "}
                  guides.
                </dd>
              </div>
              <div className="rounded-xl panel-warm bg-white p-5">
                <dt className="font-heading text-base font-black text-ink">Neuro</dt>
                <dd className="mt-2 text-sm leading-relaxed text-subtle">
                  Peptides commonly investigated in neurochemical and signalling research,
                  for example{" "}
                  <Link to="/learn/semax-vs-selank" className="text-brandblue underline underline-offset-2 hover:no-underline">Semax and Selank</Link>{" "}
                  and{" "}
                  <Link to="/learn/what-is-dsip" className="text-brandblue underline underline-offset-2 hover:no-underline">DSIP</Link>.
                </dd>
              </div>
              <div className="rounded-xl panel-warm bg-white p-5">
                <dt className="font-heading text-base font-black text-ink">Melanocortin</dt>
                <dd className="mt-2 text-sm leading-relaxed text-subtle">
                  Compounds investigated at melanocortin-family receptors, see{" "}
                  <Link to="/learn/melanotan-1-vs-2" className="text-brandblue underline underline-offset-2 hover:no-underline">Melanotan I vs II</Link>{" "}
                  and{" "}
                  <Link to="/learn/what-is-pt-141" className="text-brandblue underline underline-offset-2 hover:no-underline">PT-141</Link>.
                </dd>
              </div>
              <div className="rounded-xl panel-warm bg-white p-5">
                <dt className="font-heading text-base font-black text-ink">Mitochondrial</dt>
                <dd className="mt-2 text-sm leading-relaxed text-subtle">
                  Research compounds investigated in mitochondrial-derived-peptide and
                  mitonuclear-signalling contexts, see{" "}
                  <Link to="/learn/what-is-mots-c" className="text-brandblue underline underline-offset-2 hover:no-underline">MOTS-C</Link>.
                </dd>
              </div>
              <div className="rounded-xl panel-warm bg-white p-5 md:col-span-2">
                <dt className="font-heading text-base font-black text-ink">Peptide Blends</dt>
                <dd className="mt-2 text-sm leading-relaxed text-subtle">
                  Multi-component research formulations that combine defined amounts of
                  individual peptides in a single vial or pre-filled research device, see the{" "}
                  <Link to="/learn/peptide-blends" className="text-brandblue underline underline-offset-2 hover:no-underline">peptide blends guide</Link>{" "}
                  and comparisons such as{" "}
                  <Link to="/learn/glow-vs-klow" className="text-brandblue underline underline-offset-2 hover:no-underline">GLOW vs KLOW</Link>.
                </dd>
              </div>
            </dl>
          </section>

          <section data-testid="catalog-seo-identity">
            <p className="label-mono text-brandblue">§ Identity &amp; testing</p>
            <h2 className="mt-3 font-heading text-2xl font-black tracking-tight text-ink sm:text-3xl">
              Product Identity and Analytical Documentation
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-subtle">
              Product-level chemical identity, compound name, CAS number, sequence where
              applicable, molecular formula and molecular weight, is shown on each research
              product page. Lot / batch information and analytical documentation are the
              source of truth for a specific vial; the printed lot on the product should be
              matched to its record in the{" "}
              <Link to="/coa" className="text-brandblue underline underline-offset-2 hover:no-underline">
                COA Library
              </Link>
              . An HPLC purity result alone does not automatically establish chemical
              identity, sterility, endotoxin status or the exact peptide content of a vial,
              see the{" "}
              <Link to="/learn/how-to-read-peptide-coa" className="text-brandblue underline underline-offset-2 hover:no-underline">
                COA reading guide
              </Link>{" "}
              for what a single analytical result does and does not prove.
            </p>
          </section>

          <section data-testid="catalog-seo-guides">
            <p className="label-mono text-brandblue">§ Research guides</p>
            <div className="mt-3 flex flex-col justify-between gap-3 sm:flex-row sm:items-baseline">
              <h2 className="font-heading text-2xl font-black tracking-tight text-ink sm:text-3xl">
                Peptide Research Guides
              </h2>
              <Link
                to="/learn"
                className="font-mono text-xs font-semibold uppercase tracking-[0.12em] text-brandblue underline underline-offset-2 hover:text-brandbluedark"
                data-testid="catalog-guides-view-all"
              >
                View All Research Guides →
              </Link>
            </div>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-subtle">
              The PepDirect{" "}
              <Link to="/learn" className="text-brandblue underline underline-offset-2 hover:no-underline">
                Research Guide Library
              </Link>{" "}
              publishes separate educational pages covering individual compounds, structured
              compound comparisons, peptide blends, metabolic research and analytical /
              testing methodology. High-value comparison and methodology guides include{" "}
              <Link to="/learn/bpc-157-vs-tb-500" className="text-brandblue underline underline-offset-2 hover:no-underline">BPC-157 vs TB-500</Link>,{" "}
              <Link to="/learn/cjc-1295-dac-vs-no-dac" className="text-brandblue underline underline-offset-2 hover:no-underline">CJC-1295 DAC vs No DAC</Link>,{" "}
              <Link to="/learn/melanotan-1-vs-2" className="text-brandblue underline underline-offset-2 hover:no-underline">Melanotan I vs II</Link>{" "}
              and the{" "}
              <Link to="/learn/how-to-read-peptide-coa" className="text-brandblue underline underline-offset-2 hover:no-underline">
                COA reading guide
              </Link>.
            </p>
            <ul className="mt-6 flex flex-wrap gap-3">
              {catalogGuides.map((g) => (
                <li key={g.slug}>
                  <Link
                    to={`/learn/${g.slug}`}
                    className="btn-pill btn-outline px-5 py-2.5 text-sm font-semibold"
                    data-testid={`catalog-guide-${g.slug}`}
                  >
                    {g.title.replace(/^What Is /, "What is ").replace(/^What Are /, "What are ")}
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section data-testid="catalog-seo-faq">
            <p className="label-mono text-brandblue">§ FAQ</p>
            <h2 className="mt-3 font-heading text-2xl font-black tracking-tight text-ink sm:text-3xl">
              Research Peptide FAQs
            </h2>
            <dl className="mt-6 space-y-4">
              {CATALOG_FAQ.map(({ q, a }) => (
                <div key={q} className="rounded-xl panel-warm bg-white p-5">
                  <dt className="font-heading text-base font-black text-ink">{q}</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-subtle">{a}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>
      )}
    </div>
  );
}
